import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Tag, Divider, Select, message } from "antd";
import { useLocation } from "react-router-dom";
import { getOrdersApi, updateOrderStatusApi } from "../../services/api";

const statuses = ["All", "Pending", "Confirmed", "Shipped", "Delivered", "Cancelled"];

const statusTagColor = {
  Pending:   "orange",
  Confirmed: "blue",
  Shipped:   "purple",
  Delivered: "green",
  Cancelled: "default",
};

// Backend status → display label
const mapStatus = (status) => {
  switch ((status || "").toUpperCase()) {
    case "PENDING":    return "Pending";
    case "CONFIRMED":  return "Confirmed";
    case "SHIPPED":    return "Shipped";
    case "DELIVERED":  return "Delivered";
    case "CANCELLED":  return "Cancelled";
    default:           return "Pending";
  }
};

const ACTIONS = {
  Pending:   [{ label: "Confirm Order (admin override)", next: "CONFIRMED", type: "primary", requiresConfirm: true },
              { label: "Cancel Order",    next: "CANCELLED", type: "danger"  }],
  Confirmed: [{ label: "Cancel Order",   next: "CANCELLED", type: "danger"  }],
  Shipped:   [{ label: "Mark Delivered", next: "DELIVERED", type: "primary" },
              { label: "Cancel Order",   next: "CANCELLED", type: "danger"  }],
  Delivered: [],
  Cancelled: [],
};

export default function Orders() {
  const [orders,       setOrders]       = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [viewOrder,    setViewOrder]    = useState(null);
  const location = useLocation();
  // Guards against re-opening the modal on every later refetch (e.g. after a
  // status update) — the deep link from Dashboard's Recent Activity should
  // only auto-open once per navigation.
  const [openedFromLink, setOpenedFromLink] = useState(false);

  const fetchOrders = async () => {
    try {
      const res  = await getOrdersApi();
      const list = res.data.data || [];
      setOrders(
        list.map((o) => ({
          id:       o.orderNumber || `ORD-${o.id}`,
          rawId:    o.id,
          customer: o.customerName  || "—",
          phone:    o.customerPhone || "",
          date:     o.createdAt ? new Date(o.createdAt).toLocaleDateString() : "—",
          items:    o.totalQuantity || 0,
          total:    `₹${o.finalAmount || 0}`,
          status:   mapStatus(o.orderStatus),
          _raw:     o,
        }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  // Deep link from Dashboard's Recent Activity: navigate("/orders", { state: { openOrderId } }).
  useEffect(() => {
    const openOrderId = location.state?.openOrderId;
    if (!openOrderId || openedFromLink || orders.length === 0) return;
    const match = orders.find((o) => o.rawId === openOrderId);
    if (match) {
      setViewOrder(match._raw);
      setOpenedFromLink(true);
    }
  }, [orders, location.state, openedFromLink]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updateOrderStatusApi(orderId, newStatus);
      message.success(`Order updated to ${mapStatus(newStatus)}`);
      // Refresh list and update the modal view
      const res  = await getOrdersApi();
      const list = res.data.data || [];
      const formatted = list.map((o) => ({
        id:       o.orderNumber || `ORD-${o.id}`,
        rawId:    o.id,
        customer: o.customerName  || "—",
        phone:    o.customerPhone || "",
        date:     o.createdAt ? new Date(o.createdAt).toLocaleDateString() : "—",
        items:    o.totalQuantity || 0,
        total:    `₹${o.finalAmount || 0}`,
        status:   mapStatus(o.orderStatus),
        _raw:     o,
      }));
      setOrders(formatted);
      // Update the open modal's raw order
      const updated = list.find((o) => o.id === orderId);
      if (updated) setViewOrder(updated);
    } catch (err) {
      message.error("Failed to update status");
    }
  };

  const filtered = orders.filter(
    (o) => statusFilter === "All" || o.status === statusFilter
  );

  const columns = [
    {
      title: "Order",
      dataIndex: "id",
      render: (v) => (
        <span style={{ fontWeight: 500, fontFamily: "monospace", fontSize: 13 }}>{v}</span>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 13 }}>{v}</div>
          {r.phone && <div style={{ fontSize: 11, color: "#888" }}>{r.phone}</div>}
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => <span style={{ fontSize: 13, color: "#888" }}>{v}</span>,
    },
    {
      title: "Items",
      dataIndex: "items",
      render: (v) => <span style={{ fontSize: 13 }}>{v} item{v !== 1 ? "s" : ""}</span>,
    },
    {
      title: "Total",
      dataIndex: "total",
      render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
      title: "Payment",
      render: (_, r) => {
        const raw = r._raw || {};
        return (
          <div>
            <Tag color={raw.paymentMethod === "COD" ? "volcano" : "geekblue"}>{raw.paymentMethod || "-"}</Tag>
            {raw.refundStatus && (
              <Tag color={raw.refundStatus === "REFUNDED" ? "success" : raw.refundStatus === "FAILED" ? "error" : "gold"}>
                Refund: {raw.refundStatus}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => (
        <Tag color={statusTagColor[v] || "default"} style={{ borderRadius: 12, fontWeight: 600 }}>
          {v}
        </Tag>
      ),
    },
    {
      title: "",
      render: (_, record) => (
        <Button
          size="small"
          style={{ borderRadius: 6, fontSize: 12 }}
          onClick={() => setViewOrder(record._raw)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Orders</div>
          <div className="page-sub">{orders.length} total orders</div>
        </div>
        <Button style={{ borderRadius: 8 }}>Export CSV</Button>
      </div>

      {/* Status filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {statuses.map((s) => (
          <button
            key={s}
            className={`filter-btn${statusFilter === s ? " active" : ""}`}
            onClick={() => setStatusFilter(s)}
          >
            {s}
          </button>
        ))}
      </div>

      <Table
        rowKey="rawId"
        columns={columns}
        dataSource={filtered}
        size="small"
        pagination={{ pageSize: 10, size: "small" }}
      />

      {/* Order Detail Modal */}
      <Modal
        open={!!viewOrder}
        onCancel={() => setViewOrder(null)}
        footer={null}
        title={
          <span style={{ fontFamily: "monospace", fontWeight: 600 }}>
            {viewOrder?.orderNumber || `Order #${viewOrder?.id}`}
          </span>
        }
        width={600}
      >
        {viewOrder && (
          <OrderDetail
            order={viewOrder}
            onClose={() => setViewOrder(null)}
            onStatusChange={handleStatusUpdate}
          />
        )}
      </Modal>
    </div>
  );
}

function OrderDetail({ order, onClose, onStatusChange }) {
  const [updating, setUpdating] = useState(false);
  const displayStatus = mapStatus(order.orderStatus);
  const actions = ACTIONS[displayStatus] || [];

  const handleAction = async (nextStatus) => {
    setUpdating(true);
    await onStatusChange(order.id, nextStatus);
    setUpdating(false);
  };

  const runAction = (action) => {
    if (action.requiresConfirm) {
      Modal.confirm({
        title: "Confirm order without vendor acceptance?",
        content: "This marks the order Confirmed without waiting for vendor acceptance — vendors who haven't accepted their items yet won't have a shipment created.",
        okText: "Confirm Anyway",
        onOk: () => handleAction(action.next),
      });
    } else {
      handleAction(action.next);
    }
  };

  return (
    <div style={{ fontSize: 13 }}>

      {/* ── Status + date ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Tag
          color={statusTagColor[displayStatus] || "default"}
          style={{ fontSize: 12, padding: "3px 12px", borderRadius: 14, fontWeight: 600 }}
        >
          {displayStatus}
        </Tag>
        <span style={{ color: "#888", fontSize: 12 }}>
          {order.createdAt ? new Date(order.createdAt).toLocaleString() : "—"}
        </span>
      </div>

      {/* ── Action buttons ── */}
      {actions.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {actions.map((a) => (
            <Button
              key={a.next}
              type={a.type === "primary" ? "primary" : "default"}
              danger={a.type === "danger"}
              size="small"
              loading={updating}
              style={{ borderRadius: 8, fontWeight: 600 }}
              onClick={() => runAction(a)}
            >
              {a.label}
            </Button>
          ))}
        </div>
      )}

      {/* Hint for confirmed orders */}
      {displayStatus === "Confirmed" && (
        <div style={{ background: "#E8F4FD", border: "1px solid #90CAF9", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: "#1565C0" }}>
          Order confirmed. Shipments are created automatically once each vendor accepts their items — track progress on the <strong>Shipments</strong> page.
        </div>
      )}

      {/* ── Delivery instructions ── */}
      {order.deliveryInstructions && (
        <>
          <div style={{ background: "#F0F7FF", border: "1px solid #BAD7FF", borderRadius: 8, padding: "8px 12px", marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#1565C0", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
              Delivery Instructions
            </div>
            <div style={{ color: "#333" }}>{order.deliveryInstructions}</div>
          </div>
        </>
      )}

      {/* ── Customer ── */}
      <Section title="Customer">
        <Row label="Name"  value={order.customerName  || "—"} />
        <Row label="Phone" value={order.customerPhone || "—"} />
      </Section>

      <Divider style={{ margin: "12px 0" }} />

      {/* ── Items ── */}
      <Section title="Items">
        {!(order.items || []).length ? (
          <span style={{ color: "#aaa" }}>No items</span>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: "#888", borderBottom: "1px solid #f0f0f0" }}>
                <th style={{ textAlign: "left",   padding: "4px 8px", fontWeight: 500 }}>Product</th>
                <th style={{ textAlign: "center", padding: "4px 8px", fontWeight: 500 }}>Qty</th>
                <th style={{ textAlign: "right",  padding: "4px 8px", fontWeight: 500 }}>Unit</th>
                <th style={{ textAlign: "right",  padding: "4px 8px", fontWeight: 500 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, i) => (
                <tr key={item.id || i} style={{ borderBottom: "1px solid #fafafa" }}>
                  <td style={{ padding: "6px 8px" }}>
                    {item.product?.name || `Product #${item.product?.id}`}
                    {item.variant && (
                      <span style={{ color: "#888", marginLeft: 6, fontSize: 11 }}>
                        (variant ₹{item.variant.price})
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: "center", padding: "6px 8px" }}>{item.quantity}</td>
                  <td style={{ textAlign: "right",  padding: "6px 8px" }}>₹{item.price}</td>
                  <td style={{ textAlign: "right",  padding: "6px 8px", fontWeight: 500 }}>
                    ₹{item.lineTotal ?? (item.price * item.quantity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Divider style={{ margin: "12px 0" }} />

      {/* ── Price breakdown ── */}
      <Section title="Price Breakdown">
        <Row label="Item Total" value={`₹${order.subTotal ?? 0}`} />
        <Row label="Delivery"   value={(order.deliveryCharge ?? 0) === 0 ? "FREE" : `₹${order.deliveryCharge}`} valueColor={(order.deliveryCharge ?? 0) === 0 ? "#43A047" : undefined} />
        {(order.discountAmount ?? 0) > 0 && (
          <Row label="Discount" value={`-₹${order.discountAmount}`} valueColor="#43A047" />
        )}
        <Divider style={{ margin: "6px 0" }} />
        <Row label="Total" value={`₹${order.finalAmount ?? 0}`} bold />
      </Section>

      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, bold, valueColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
      <span style={{ color: "#666" }}>{label}</span>
      <span style={{ fontWeight: bold ? 600 : 400, color: valueColor || "#111" }}>{value}</span>
    </div>
  );
}
